import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Search, Filter, X, Loader2,
  ChevronDown, CheckCircle2, ThumbsUp, Lightbulb, User2,
  Clock, RefreshCw, Trash2, Ban, HelpCircle, XCircle, Upload,
  Phone, BookOpen, CalendarDays, PlusCircle, MinusCircle
} from 'lucide-react';
import api from '../../api';
import { supabase } from '../../lib/supabase';
import { cn } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { BulkImportModal } from '../ui/BulkImportModal';
import { Button } from '../ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TAG_CONFIG = {
  positive: {
    label: 'Positive',
    icon: ThumbsUp,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    dot: 'bg-emerald-500',
  },
  convincing: {
    label: 'Convincing',
    icon: Lightbulb,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    dot: 'bg-amber-500',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
  },
  inquiry: {
    label: 'Inquiry',
    icon: HelpCircle,
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    border: 'border-indigo-200 dark:border-indigo-800',
    dot: 'bg-indigo-500',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
    dot: 'bg-rose-500',
  },
};

function TagBadge({ tag, size = 'sm', onClick }) {
  const cfg = TAG_CONFIG[tag] || TAG_CONFIG.positive;
  const Icon = cfg.icon;
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-semibold transition-all',
        cfg.bg, cfg.border, cfg.color,
        size === 'sm' ? 'px-2.5 py-0.5 text-[10px]' : 'px-3 py-1 text-xs',
        onClick ? 'cursor-pointer hover:opacity-80 active:scale-95' : 'cursor-default'
      )}
    >
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {cfg.label}
    </button>
  );
}

function TagDropdown({ currentTag, onSelect, onClose }) {
  return (
    <>
      {/* Backdrop for easy closing */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      
      <div className="absolute z-50 right-0 top-full mt-2 w-52 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200 origin-top-right ring-1 ring-black/5 dark:ring-white/5">
        <div className="px-3.5 py-2 border-b border-zinc-100 dark:border-zinc-800/50">
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Update Status</p>
        </div>
        
        <div className="p-1.5">
          {Object.entries(TAG_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const isSelected = currentTag === key;
            return (
              <button
                key={key}
                onClick={() => { onSelect(key); onClose(); }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 group/item',
                  isSelected
                    ? `${cfg.bg} ${cfg.color} shadow-sm border border-zinc-200/20`
                    : 'hover:bg-zinc-100 dark:hover:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400'
                )}
              >
                <div className={cn(
                  "w-7 h-7 rounded-lg flex items-center justify-center transition-transform group-hover/item:scale-110",
                  isSelected ? "bg-white dark:bg-zinc-800/50" : "bg-zinc-50 dark:bg-zinc-800/30"
                )}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex flex-col items-start gap-0.5">
                  <span className={isSelected ? 'text-zinc-900 dark:text-zinc-50' : ''}>
                    {cfg.label}
                  </span>
                  {isSelected && (
                    <span className="text-[9px] opacity-60 font-medium">Currently active</span>
                  )}
                </div>
                {isSelected && (
                  <div className="ml-auto">
                    <CheckCircle2 className="w-3.5 h-3.5 opacity-60" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function InquiryCard({ inquiry, onTagChange, onDelete }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleTagSelect = async (newTag) => {
    if (newTag === inquiry.tag) return;
    setUpdating(true);
    try {
      const res = await api.patch(`/api/inquiries/${inquiry.id}/tag`, { tag: newTag });
      onTagChange(res.data);
      toast.success('Tag updated');
    } catch {
      toast.error('Could not update tag');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/inquiries/${inquiry.id}`);
      onDelete(inquiry.id);
      toast.success('Inquiry deleted');
    } catch {
      toast.error('Could not delete inquiry');
      setDeleting(false);
    }
  };

  const formattedDate = useMemo(() => {
    if (!inquiry.created_at) return '';
    const d = new Date(inquiry.created_at);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, [inquiry.created_at]);

  const contactNumbers = Array.isArray(inquiry.contact_numbers) ? inquiry.contact_numbers : [];

  return (
    <div className="group relative bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl p-4 hover:border-zinc-300 dark:hover:border-zinc-700 hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 shrink-0 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-sm font-bold text-zinc-500 uppercase">
            {inquiry.name?.charAt(0) || '?'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-zinc-900 dark:text-zinc-50 truncate">{inquiry.name}</p>
            {/* Primary contact */}
            {inquiry.contact && (
              <p className="text-[10px] text-zinc-400 truncate mt-0.5">{inquiry.contact}</p>
            )}
          </div>
        </div>

        {/* Tag control & Delete — right aligned */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            {updating ? (
              <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
            ) : (
              <div className="flex items-center gap-1">
                <TagBadge tag={inquiry.tag} onClick={() => setDropdownOpen(o => !o)} />
                <button
                  onClick={() => setDropdownOpen(o => !o)}
                  className="text-zinc-300 hover:text-zinc-500 transition-colors"
                  title="Change tag"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
            )}
            {dropdownOpen && (
              <TagDropdown
                currentTag={inquiry.tag}
                onSelect={handleTagSelect}
                onClose={() => setDropdownOpen(false)}
              />
            )}
          </div>

          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all lg:opacity-0 lg:group-hover:opacity-100 disabled:opacity-50"
            title="Delete inquiry"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Extra contact numbers */}
      {contactNumbers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {contactNumbers.map((num, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-md px-2 py-0.5 text-[10px] font-medium">
              <Phone className="w-2.5 h-2.5" />
              {num}
            </span>
          ))}
        </div>
      )}

      {/* Course name */}
      {inquiry.course_name && (
        <div className="flex items-center gap-1 mb-2">
          <BookOpen className="w-3 h-3 text-indigo-400 shrink-0" />
          <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium truncate">{inquiry.course_name}</span>
        </div>
      )}

      {/* Description */}
      {inquiry.description && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed line-clamp-2 mb-3">
          {inquiry.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-zinc-400 pt-2 border-t border-zinc-50 dark:border-zinc-800">
        <span className="flex items-center gap-1">
          <User2 className="w-3 h-3" />
          {inquiry.added_by || 'Unknown'}
        </span>
        <span className="flex items-center gap-1">
          <CalendarDays className="w-3 h-3" />
          {inquiry.inquiry_date
            ? new Date(inquiry.inquiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : formattedDate}
        </span>
      </div>
    </div>
  );
}

const EMPTY_FORM = { name: '', contact: '', contact_numbers: [''], course_name: '', description: '', tag: 'positive', inquiry_date: '' };

export default function InquiriesPanel() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/inquiries');
      setInquiries(res.data || []);
    } catch {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      // Filter out blank contact number entries before submission
      const payload = {
        ...form,
        contact_numbers: form.contact_numbers.filter(n => n.trim() !== ''),
      };
      const res = await api.post('/api/inquiries', payload);
      const newInq = res.data;
      setInquiries(prev => [newInq, ...prev]);
      setForm(EMPTY_FORM);
      setShowForm(false);
      toast.success('Inquiry logged successfully');
    } catch {
      toast.error('Failed to log inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTagChange = useCallback((updated) => {
    setInquiries(prev => prev.map(i => i.id === updated.id ? updated : i));
  }, []);

  const handleDelete = useCallback((id) => {
    setInquiries(prev => prev.filter(i => i.id !== id));
  }, []);

  const filtered = useMemo(() => {
    return inquiries.filter(i => {
      const matchTag = filterTag === 'all' || i.tag === filterTag;
      const q = search.toLowerCase();
      const matchSearch = !q
        || i.name?.toLowerCase().includes(q)
        || i.contact?.toLowerCase().includes(q)
        || i.course_name?.toLowerCase().includes(q)
        || i.description?.toLowerCase().includes(q)
        || (Array.isArray(i.contact_numbers) && i.contact_numbers.some(n => n.toLowerCase().includes(q)));
      return matchTag && matchSearch;
    });
  }, [inquiries, filterTag, search]);

  // Tag counts for the stats bar
  const tagCounts = useMemo(() => {
    const c = {};
    Object.keys(TAG_CONFIG).forEach(k => c[k] = 0);
    inquiries.forEach(i => { if (c[i.tag] !== undefined) c[i.tag]++; });
    return c;
  }, [inquiries]);

  return (
    <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-950/50 overflow-hidden">
      <BulkImportModal
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        entityName="inquiries"
        onImportComplete={() => { fetchInquiries(); }}
      />
      {/* ── Header ── */}
      <div className="px-4 md:px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50 flex items-center gap-2 tracking-tight">
              <MessageSquare className="w-5 h-5 text-blue-500 shrink-0" />
              <span className="truncate">Inquiry Threads</span>
            </h2>
            <p className="text-[11px] text-zinc-400 mt-0.5 truncate">Log, track, and manage all inquiry interactions</p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={fetchInquiries}
              className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setBulkImportOpen(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 transition-all active:scale-95 shadow-sm whitespace-nowrap"
              title="Bulk Import"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Import</span>
            </button>
            <button
              onClick={() => setShowForm(o => !o)}
              className={cn(
                'flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-sm whitespace-nowrap',
                showForm
                  ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20'
              )}
            >
              {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              <span>{showForm ? 'Cancel' : 'New Inquiry'}</span>
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {Object.entries(TAG_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setFilterTag(prev => prev === key ? 'all' : key)}
              className={cn(
                'flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-lg border text-[10px] sm:text-[11px] font-semibold transition-all',
                filterTag === key ? `${cfg.bg} ${cfg.border} ${cfg.color}` : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-500 hover:border-zinc-300 dark:hover:border-zinc-700'
              )}
            >
              <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', cfg.dot)} />
              <span className="whitespace-nowrap">{cfg.label}</span>
              <span className="font-black tabular-nums">{tagCounts[key]}</span>
            </button>
          ))}
          <div className="ml-auto text-[10px] sm:text-[11px] text-zinc-400 font-medium whitespace-nowrap">
            {filtered.length}/{inquiries.length}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-6 space-y-5 max-w-5xl mx-auto">
          {/* ── New Inquiry Form ── */}
          {showForm && (
            <div className="bg-white dark:bg-zinc-900 border border-blue-200 dark:border-blue-800 rounded-xl p-5 shadow-sm animate-in slide-in-from-top-2 duration-200">
              <h3 className="text-sm font-bold text-zinc-800 dark:text-zinc-100 mb-4 flex items-center gap-2">
                <Plus className="w-4 h-4 text-blue-500" />
                New Inquiry
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                      Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Full name of inquirer"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                      Inquiry Date
                    </label>
                    <input
                      type="date"
                      value={form.inquiry_date}
                      onChange={e => setForm(f => ({ ...f, inquiry_date: e.target.value }))}
                      className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                    />
                  </div>
                </div>

                {/* Course Name */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                    Course Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. B.Tech CSE, MBA Finance"
                    value={form.course_name}
                    onChange={e => setForm(f => ({ ...f, course_name: e.target.value }))}
                    className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                  />
                </div>

                {/* Primary Contact + Multiple Numbers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                      Primary Contact
                    </label>
                    <input
                      type="text"
                      placeholder="Email or main handle"
                      value={form.contact}
                      onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                      className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                      Phone Numbers
                    </label>
                    <div className="space-y-2">
                      {form.contact_numbers.map((num, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                          <input
                            type="tel"
                            placeholder={`Number ${idx + 1}`}
                            value={num}
                            onChange={e => {
                              const updated = [...form.contact_numbers];
                              updated[idx] = e.target.value;
                              setForm(f => ({ ...f, contact_numbers: updated }));
                            }}
                            className="flex-1 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg py-1.5 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                          />
                          {form.contact_numbers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = form.contact_numbers.filter((_, i) => i !== idx);
                                setForm(f => ({ ...f, contact_numbers: updated }));
                              }}
                              className="text-zinc-400 hover:text-red-500 transition-colors shrink-0"
                              title="Remove number"
                            >
                              <MinusCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, contact_numbers: [...f.contact_numbers, ''] }))}
                        className="flex items-center gap-1.5 text-[11px] text-blue-500 hover:text-blue-600 font-semibold mt-1 transition-colors"
                      >
                        <PlusCircle className="w-3.5 h-3.5" />
                        Add another number
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Summarise the inquiry details..."
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                  <div className="min-w-0">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
                      Initial Tag
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(TAG_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, tag: key }))}
                          className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-semibold transition-all whitespace-nowrap',
                            form.tag === key
                              ? `${cfg.bg} ${cfg.border} ${cfg.color} shadow-sm`
                              : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:border-zinc-300'
                          )}
                        >
                          <cfg.icon className="w-3 h-3 shrink-0" />
                          <span>{cfg.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-sm shadow-blue-500/20 w-full sm:w-auto"
                  >
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    <span>Log Inquiry</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Search ── */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by name, contact or description…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-3.5 h-3.5 text-zinc-400 hover:text-zinc-600" />
              </button>
            )}
          </div>

          {/* ── Inquiry grid ── */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm text-zinc-400">Loading inquiries…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-800 text-center gap-3">
              <div className="w-14 h-14 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center">
                <MessageSquare className="w-7 h-7 text-zinc-300 dark:text-zinc-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-zinc-600 dark:text-zinc-400">No inquiries found</p>
                <p className="text-[11px] text-zinc-400 mt-1">
                  {search || filterTag !== 'all' ? 'Try adjusting your filters' : 'Create the first inquiry using the button above'}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(inq => (
                <InquiryCard key={inq.id} inquiry={inq} onTagChange={handleTagChange} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
